import React, { useEffect, useReducer, useRef, useState } from 'react';
import { Row, Spinner } from 'react-bootstrap';
import { isMobile } from 'react-device-detect';
import ReactHtmlParser from 'react-html-parser';
import { useTranslation } from 'react-i18next';
import {
  BiArrowToRight,
  BiAt,
  BiBrain,
  BiFastForwardCircle,
  BiFile,
  BiHome,
  BiInfoCircle,
  BiLeftArrowAlt,
  BiLinkExternal,
  BiLogInCircle,
  BiLogoJavascript,
  BiMessageRoundedDots,
  BiMicrophone,
  BiPaperclip,
  BiPlay,
  BiPlus,
  BiShuffle,
  BiSlideshow,
  BiSolidBot,
  BiSolidExit,
  BiSolidHome,
  BiSolidImageAlt,
  BiSolidMegaphone,
  BiSolidPlusCircle,
  BiSolidShapes,
  BiSolidSpreadsheet,
  BiSolidTimeFive,
  BiSolidUserVoice,
  BiToggleLeft,
  BiUndo,
  BiUser,
  BiUserCheck,
  BiUserPlus,
  BiX,
  BiZoomIn,
  BiZoomOut,
} from 'react-icons/bi';
import { FaMapSigns } from 'react-icons/fa';
import { MdCompress } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { TransformComponent, TransformWrapper, useControls } from 'react-zoom-pan-pinch';

import { useAppDispatch, useAppSelector } from '@/app/hook';
import { ChatbotRTKService } from '@/app/services/chatbot-rtk.services';
import { useGetScenarioDetailQuery, useModifyScenarioMutation, usePublishScenariosMutation } from '@/app/services/scenario-rtk.service';
import { RootState } from '@/app/store';
import { confirmWithDialog } from '@/components/common/confirmation/confirm-dialog';
import { PSButton } from '@/components/ui/button';
import { AlertsType } from '@/constants/alerts.constant';
import { MultiLanguageUrl } from '@/constants/multiLanguage.constant';
import { handleSetBotStatus, handleSetLivePreviewTargetBot } from '@/features/bot/botSlice';
import { selectClient, setIsFloatSideMenuShow, setSideMenuSize } from '@/features/dashboard.slice';
import { selectKnowledgeCreate, setChatBotService, setEditScenario } from '@/features/knowledge.slice';
import { addUndoRedoStep, resetUndoRedoScenario, setCurrentScenarioTitle } from '@/features/scenario.slice';
import { handleToggleShowUpgradePlanModal } from '@/features/ui/uiSlice';
import { BlockType } from '@/interfaces/models/Knowledge/scenario.interface';
import { BotTypeEnum } from '@/utils/BotTypeEnum';

import IUpdateKnowledgeAssistant from '../../../../../interfaces/models/Knowledge/update-knowledge-assistant.interface';
import Utilities from '../../../../../utils';
import Alert from '../../../../common/Alert';
import { MakeIcon } from '../../../../common/icons/make.icon';
import { SuperUserManagerIcon } from '../../../../common/icons/superUserManager.icon';
import VariableIcon from '../../../../common/icons/variable.icon';
import LoadingComponent from '../../../../common/loading/loading.component';
import { IRenderCard } from '../../../../common/react-drag-hierarchy-tree-main/src/components/interfaces';
import OrgTreeComponent, { useTree } from '../../../../common/react-drag-hierarchy-tree-main/src/index';
import { ENestedObjectType, INestedObject, IRenderButton } from '../../../../common/react-drag-hierarchy-tree-main/src/interfaces';
import ScenarioYoutubeVideo from '../../../../common/ScenarioYoutubeVideo';
import ProBadge from '../../../../ui/ProBadge';
import CreateScenarioTooltip from '../../CreateScenarioTooltip';
import useMacBotActions from '../../slice/useMacBotActions';
import { CheckIfUserExistsForm } from '../../sum/CheckIfUserExistsForm';
import { EditRegisterUserForm } from '../../sum/EditRegisterUserForm';
import { GetUserInfoForm } from '../../sum/GetUserInfoForm';
import { UserCredentialForm } from '../../sum/UserCredentialForm';
import { MacBotASKToAIForm } from './scenario-ask-to-ai-form.component';
import { MacBotAttachmentForm } from './scenario-attachment-form.component';
import { MacBotButtonForm } from './scenario-button-form.component';
import { MacBotConditionForm } from './scenario-condition-form.component';
import { MacBotCreateVariableForm } from './scenario-create-variable-form.component';
import { DelayForm } from './scenario-delay-form.componen';
import { EndConversationForm } from './scenario-end-conversation-form.component';
import { MacBotGotoForm } from './scenario-goto-form.component';
import { MacBotHTTPRequestForm } from './scenario-http-request-form.component';
import { MacBotJSCodeForm } from './scenario-js-code-form.component';
import { MacBotLeadForm } from './scenario-lead-form.component';
import { MacBotMakeWebHookForm } from './scenario-make-webhook.component';
import { MacBotMediaForm } from './scenario-media-form.component';
import { MacBotNotifyMeByEmailForm } from './scenario-NotifyMeByEmail-form.component';
import { MacBotOverrideVariableForm } from './scenario-override-variable-form.component';
import { MacBotPassToAIForm } from './scenario-pass-to-ai-form.component';
import { MacBotReceiveAttachmentForm } from './scenario-ReceiveAttachment-form.component';
import { MacBotResponseForm } from './scenario-response-form.component';
import { MacBotSpeechRecordingForm } from './scenario-SpeechRecording-form.component';
import { MacBotSummarizeConversationForm } from './scenario-summarize-conversation-form.component';
import { SuperUserManagerServicesForm } from './scenario-super-user-manager-services-form.component';
import { MacBotSwitchToChatbotForm } from './scenario-switch-to-chatbot-form.component';
import { MacBotUserInputForm } from './scenario-user-input-form.component';
import { MacBotVariableForm } from './scenario-variable-form.component';
import { TextScenarioLinkForm } from './text-scenario-link-form.component';
import TreeNode from './TreeNode';

export type AcceptableItemsToAddType =
  | 'START_POINT'
  | 'BOT_MESSAGE'
  | 'READ_USER_MESSAGE'
  | 'ADD_BUTTON'
  | 'ADD_LINK'
  | 'ASSIGN_VARIABLE'
  | 'HTTP_REQUEST'
  | 'MAKE_WEBHOOK'
  | 'CONDITIONS'
  | 'CONDITION_ELSE'
  | 'OVERRIDE_VARIABLE'
  | 'CREATE_VARIABLE'
  | 'SEND_ATTACHMENT'
  | 'SEND_MEDIA'
  | 'LEAD_FORM'
  | 'PASS_TO_AI'
  | 'SWITCH_TO_CHATBOT'
  | 'END_OF_CONVERSATION'
  | 'NOTIFY_ME_BY_EMAIL'
  | 'RECEIVE_ATTACHMENT'
  | 'DELAY'
  | 'GO_TO'
  | 'ELSE'
  | 'SUPER_USER_MANAGER_SERVICES'
  | 'CHECK_IF_USER_EXISTS'
  | 'USER_CREDENTIAL'
  | 'GET_USER_INFO'
  | 'ASK_TO_AI'
  | 'SPEECH_RECORDING'
  | 'SUMMARIZE_CONVERSATION'
  | 'JS_CODE'
  | 'REGISTER_USER';

export const acceptableBlocksMap: Partial<Record<ENestedObjectType, string[]>> = {
  [ENestedObjectType.Start]: ['BOT_MESSAGE'],
  [ENestedObjectType.Response]: [
    'BOT_MESSAGE',
    'SUMMARIZE_CONVERSATION',
    'NOTIFY_ME_BY_EMAIL',
    'RECEIVE_ATTACHMENT',
    'SPEECH_RECORDING',
    'READ_USER_MESSAGE',
    'ADD_BUTTON',
    'ADD_LINK',
    'ASSIGN_VARIABLE',
    'HTTP_REQUEST',
    'JS_CODE',
    'MAKE_WEBHOOK',
    'CONDITIONS',
    'PASS_TO_AI',
    'ASK_TO_AI',
    'OVERRIDE_VARIABLE',
    'CREATE_VARIABLE',
    'SEND_MEDIA',
    'SEND_ATTACHMENT',
    'LEAD_FORM',
    'END_OF_CONVERSATION',
    'DELAY',
    'GO_TO',
    'SUPER_USER_MANAGER_SERVICES',
    'USER_CREDENTIAL',
    'REGISTER_USER',
    'GET_USER_INFO',
    'CHECK_IF_USER_EXISTS',
  ],
  [ENestedObjectType.SummarizeConversation]: [
    'BOT_MESSAGE',
    'SEND_MEDIA',
    'SEND_ATTACHMENT',
    'RECEIVE_ATTACHMENT',
    'CREATE_VARIABLE',
    'OVERRIDE_VARIABLE',
    'CONDITIONS',
    'JS_CODE',
    'HTTP_REQUEST',
    'MAKE_WEBHOOK',
    'NOTIFY_ME_BY_EMAIL',
    'PASS_TO_AI',
    'ASK_TO_AI',
    'GO_TO',
  ],
  [ENestedObjectType.PassToAI]: [],
  [ENestedObjectType.AskToAI]: [
    'BOT_MESSAGE',
    'SEND_MEDIA',
    'SEND_ATTACHMENT',
    'RECEIVE_ATTACHMENT',
    'ADD_BUTTON',
    'ADD_LINK',
    'CREATE_VARIABLE',
    'ASSIGN_VARIABLE',
    'OVERRIDE_VARIABLE',
    'CONDITIONS',
    'JS_CODE',
    'HTTP_REQUEST',
    'MAKE_WEBHOOK',
    'NOTIFY_ME_BY_EMAIL',
    'LEAD_FORM',
    'PASS_TO_AI',
    'SUMMARIZE_CONVERSATION',
    'GO_TO',
    'END_OF_CONVERSATION',
  ],
  [ENestedObjectType.SwitchToChatbot]: [],
  [ENestedObjectType.NotifyMeByEmail]: [
    'BOT_MESSAGE',
    'SEND_MEDIA',
    'SEND_ATTACHMENT',
    'RECEIVE_ATTACHMENT',
    'OVERRIDE_VARIABLE',
    'CREATE_VARIABLE',
    'END_OF_CONVERSATION',
    'GO_TO',
  ],
  [ENestedObjectType.ReceiveAttachment]: [
    'BOT_MESSAGE',
    'NOTIFY_ME_BY_EMAIL',
    'HTTP_REQUEST',
    'JS_CODE',
    'MAKE_WEBHOOK',
    'CONDITIONS',
    'PASS_TO_AI',
    'ASK_TO_AI',
    'OVERRIDE_VARIABLE',
    'CREATE_VARIABLE',
    'SEND_MEDIA',
    'DELAY',
    'SEND_ATTACHMENT',
    'END_OF_CONVERSATION',
    'GO_TO',
  ],
  [ENestedObjectType.SpeechRecording]: [
    'BOT_MESSAGE',
    'SUMMARIZE_CONVERSATION',
    'NOTIFY_ME_BY_EMAIL',
    'HTTP_REQUEST',
    'JS_CODE',
    'MAKE_WEBHOOK',
    'CONDITIONS',
    'PASS_TO_AI',
    'ASK_TO_AI',
    'OVERRIDE_VARIABLE',
    'CREATE_VARIABLE',
    'SEND_MEDIA',
    'SEND_ATTACHMENT',
    'GO_TO',
  ],
  [ENestedObjectType.Else]: [
    'BOT_MESSAGE',
    'SUMMARIZE_CONVERSATION',
    'NOTIFY_ME_BY_EMAIL',
    'RECEIVE_ATTACHMENT',
    'ADD_BUTTON',
    'ADD_LINK',
    'HTTP_REQUEST',
    'JS_CODE',
    'MAKE_WEBHOOK',
    'CONDITIONS',
    'PASS_TO_AI',
    'ASK_TO_AI',
    'OVERRIDE_VARIABLE',
    'CREATE_VARIABLE',
    'SEND_MEDIA',
    'SEND_ATTACHMENT',
    'GO_TO',
  ],
  [ENestedObjectType.UserInput]: ['BOT_MESSAGE', 'CONDITIONS', 'SWITCH_TO_CHATBOT', 'OVERRIDE_VARIABLE', 'CREATE_VARIABLE', 'GO_TO'],
  [ENestedObjectType.Variable]: [
    'BOT_MESSAGE',
    'SUMMARIZE_CONVERSATION',
    'NOTIFY_ME_BY_EMAIL',
    'RECEIVE_ATTACHMENT',
    'HTTP_REQUEST',
    'JS_CODE',
    'MAKE_WEBHOOK',
    'CONDITIONS',
    'PASS_TO_AI',
    'ASK_TO_AI',
    'OVERRIDE_VARIABLE',
    'CREATE_VARIABLE',
    'GO_TO',
  ],
  [ENestedObjectType.Goto]: ['BOT_MESSAGE'],
  [ENestedObjectType.Button]: [
    'BOT_MESSAGE',
    'HTTP_REQUEST',
    'JS_CODE',
    'MAKE_WEBHOOK',
    'CONDITIONS',
    'SWITCH_TO_CHATBOT',
    'PASS_TO_AI',
    'ASK_TO_AI',
    'OVERRIDE_VARIABLE',
    'CREATE_VARIABLE',
    'SEND_MEDIA',
    'SEND_ATTACHMENT',
    'RECEIVE_ATTACHMENT',
    'LEAD_FORM',
    'GO_TO',
  ],
  [ENestedObjectType.TextLink]: ['END_OF_CONVERSATION'],
  [ENestedObjectType.JSCode]: [
    'BOT_MESSAGE',
    'NOTIFY_ME_BY_EMAIL',
    'HTTP_REQUEST',
    'MAKE_WEBHOOK',
    'CONDITIONS',
    'PASS_TO_AI',
    'ASK_TO_AI',
    'OVERRIDE_VARIABLE',
    'CREATE_VARIABLE',
    'SEND_MEDIA',
    'SEND_ATTACHMENT',
    'GO_TO',
  ],
  [ENestedObjectType.Http]: [
    'BOT_MESSAGE',
    'HTTP_REQUEST',
    'JS_CODE',
    'MAKE_WEBHOOK',
    'CONDITIONS',
    'PASS_TO_AI',
    'ASK_TO_AI',
    'OVERRIDE_VARIABLE',
    'CREATE_VARIABLE',
    'SEND_MEDIA',
    'SEND_ATTACHMENT',
    'LEAD_FORM',
    'END_OF_CONVERSATION',
    'GO_TO',
  ],
  [ENestedObjectType.MakeWebHook]: [
    'BOT_MESSAGE',
    'HTTP_REQUEST',
    'JS_CODE',
    'MAKE_WEBHOOK',
    'CONDITIONS',
    'PASS_TO_AI',
    'ASK_TO_AI',
    'OVERRIDE_VARIABLE',
    'CREATE_VARIABLE',
    'SEND_MEDIA',
    'SEND_ATTACHMENT',
    'LEAD_FORM',
    'END_OF_CONVERSATION',
    'GO_TO',
  ],
  [ENestedObjectType.Condition]: [
    'BOT_MESSAGE',
    'SUMMARIZE_CONVERSATION',
    'NOTIFY_ME_BY_EMAIL',
    'RECEIVE_ATTACHMENT',
    'ADD_BUTTON',
    'ADD_LINK',
    'HTTP_REQUEST',
    'JS_CODE',
    'MAKE_WEBHOOK',
    'CONDITIONS',
    'PASS_TO_AI',
    'ASK_TO_AI',
    'OVERRIDE_VARIABLE',
    'CREATE_VARIABLE',
    'SEND_MEDIA',
    'SEND_ATTACHMENT',
    'GO_TO',
  ],
  [ENestedObjectType.ConditionElse]: [
    'BOT_MESSAGE',
    'SUMMARIZE_CONVERSATION',
    'NOTIFY_ME_BY_EMAIL',
    'RECEIVE_ATTACHMENT',
    'ADD_BUTTON',
    'ADD_LINK',
    'HTTP_REQUEST',
    'JS_CODE',
    'MAKE_WEBHOOK',
    'CONDITIONS',
    'PASS_TO_AI',
    'ASK_TO_AI',
    'OVERRIDE_VARIABLE',
    'CREATE_VARIABLE',
    'SEND_MEDIA',
    'SEND_ATTACHMENT',
    'GO_TO',
  ],
  [ENestedObjectType.Delay]: ['BOT_MESSAGE', 'RECEIVE_ATTACHMENT', 'SEND_MEDIA', 'SEND_ATTACHMENT', 'END_OF_CONVERSATION'],
  [ENestedObjectType.EndOfConversation]: [],
  [ENestedObjectType.OverrideVariable]: [
    'BOT_MESSAGE',
    'RECEIVE_ATTACHMENT',
    'HTTP_REQUEST',
    'JS_CODE',
    'MAKE_WEBHOOK',
    'ASK_TO_AI',
    'OVERRIDE_VARIABLE',
    'CREATE_VARIABLE',
    'SEND_MEDIA',
    'SEND_ATTACHMENT',
    'LEAD_FORM',
    'GO_TO',
  ],
  [ENestedObjectType.CreateVariable]: [
    'BOT_MESSAGE',
    'SUMMARIZE_CONVERSATION',
    'NOTIFY_ME_BY_EMAIL',
    'RECEIVE_ATTACHMENT',
    'HTTP_REQUEST',
    'JS_CODE',
    'MAKE_WEBHOOK',
    'CONDITIONS',
    'PASS_TO_AI',
    'OVERRIDE_VARIABLE',
    'CREATE_VARIABLE',
    'SEND_MEDIA',
    'SEND_ATTACHMENT',
    'LEAD_FORM',
    'GO_TO',
  ],
  [ENestedObjectType.Lead]: [
    'BOT_MESSAGE',
    'SUMMARIZE_CONVERSATION',
    'NOTIFY_ME_BY_EMAIL',
    'RECEIVE_ATTACHMENT',
    'HTTP_REQUEST',
    'JS_CODE',
    'MAKE_WEBHOOK',
    'CONDITIONS',
    'PASS_TO_AI',
    'ASK_TO_AI',
    'OVERRIDE_VARIABLE',
    'SWITCH_TO_CHATBOT',
    'CREATE_VARIABLE',
    'SEND_MEDIA',
    'SEND_ATTACHMENT',
    'GO_TO',
  ],
  [ENestedObjectType.Media]: [
    'BOT_MESSAGE',
    'SUMMARIZE_CONVERSATION',
    'RECEIVE_ATTACHMENT',
    'ADD_BUTTON',
    'ADD_LINK',
    'HTTP_REQUEST',
    'JS_CODE',
    'MAKE_WEBHOOK',
    'CONDITIONS',
    'PASS_TO_AI',
    'ASK_TO_AI',
    'OVERRIDE_VARIABLE',
    'CREATE_VARIABLE',
    'SEND_MEDIA',
    'SEND_ATTACHMENT',
    'END_OF_CONVERSATION',
    'DELAY',
    'GO_TO',
  ],
  [ENestedObjectType.Attachment]: [
    'BOT_MESSAGE',
    'RECEIVE_ATTACHMENT',
    'CONDITIONS',
    'ASK_TO_AI',
    'OVERRIDE_VARIABLE',
    'CREATE_VARIABLE',
    'SEND_MEDIA',
    'SEND_ATTACHMENT',
    'END_OF_CONVERSATION',
    'DELAY',
    'GO_TO',
  ],
  [ENestedObjectType.SuperUserManagerServices]: [
    'BOT_MESSAGE',
    'HTTP_REQUEST',
    'JS_CODE',
    'MAKE_WEBHOOK',
    'CONDITIONS',
    'PASS_TO_AI',
    'ASK_TO_AI',
    'OVERRIDE_VARIABLE',
    'CREATE_VARIABLE',
    'GO_TO',
  ],
  [ENestedObjectType.RegisterUser]: [
    'BOT_MESSAGE',
    'HTTP_REQUEST',
    'JS_CODE',
    'MAKE_WEBHOOK',
    'CONDITIONS',
    'PASS_TO_AI',
    'ASK_TO_AI',
    'OVERRIDE_VARIABLE',
    'CREATE_VARIABLE',
    'GO_TO',
  ],
  [ENestedObjectType.GetUserProfile]: [
    'BOT_MESSAGE',
    'HTTP_REQUEST',
    'JS_CODE',
    'MAKE_WEBHOOK',
    'CONDITIONS',
    'PASS_TO_AI',
    'ASK_TO_AI',
    'OVERRIDE_VARIABLE',
    'CREATE_VARIABLE',
    'GO_TO',
  ],
  [ENestedObjectType.CheckIfUserExists]: [
    'BOT_MESSAGE',
    'HTTP_REQUEST',
    'JS_CODE',
    'MAKE_WEBHOOK',
    'CONDITIONS',
    'PASS_TO_AI',
    'ASK_TO_AI',
    'OVERRIDE_VARIABLE',
    'CREATE_VARIABLE',
    'GO_TO',
  ],
  [ENestedObjectType.UserLoginForm]: [
    'BOT_MESSAGE',
    'HTTP_REQUEST',
    'JS_CODE',
    'MAKE_WEBHOOK',
    'CONDITIONS',
    'PASS_TO_AI',
    'ASK_TO_AI',
    'OVERRIDE_VARIABLE',
    'CREATE_VARIABLE',
    'GO_TO',
  ],
};

const TextScenarioComponent = ({
  hasUnpublishedData,
  setHasUnpublishedData,
}: {
  hasUnpublishedData: boolean;
  setHasUnpublishedData: (value: boolean) => void;
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const createState = useAppSelector(selectKnowledgeCreate);
  const client = useAppSelector(selectClient);
  const currentScenarioId = useAppSelector(state => state.bots.wizard.id);
  const { botConfigs } = useAppSelector(selectClient);

  const [textScenarioAllowedBlocks, setTextScenarioAllowedBlocks] = useState(
    botConfigs ? botConfigs.find((config: any) => config.botName === BotTypeEnum.TEXT_SCENARIO).config.allowedBlocks : []
  );

  const [scale, setScale] = useState<number>(1);
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [isLivePreview, setIsLivePreview] = useState(false);
  const [isLivePreviewLoading, setIsLivePreviewLoading] = useState(false);
  // const [fetChGetScenarioDetail, setFetchGetScenarioDetail] = useState(false);
  const [treeData, setTreeData] = useState<any[]>([]);
  const [configData, setConfigData] = useState<any>(null);
  const [showPlusModal, setShowPlusModal] = useState(false);
  const [acceptableBlocksToAdd, setAcceptableBlocksToAdd] = useState<Array<AcceptableItemsToAddType>>([]);
  const [targetBlockToAddToId, setTargetBlockToAddToId] = useState('');

  const macBotState = useSelector((state: RootState) => state.macBot);

  const {
    setShowResponse,
    setShowUserInput,
    setShowGoto,
    setShowSpeechRecording,
    setShowAskToAI,
    setShowAttachment,
    setShowButton,
    setShowCheckIfUserExists,
    setShowCondition,
    setShowCreateVariable,
    setShowDelay,
    setShowElse,
    setShowEndConversation,
    setShowGetUserInfo,
    setShowHTTPRequest,
    setShowJSCode,
    setShowLead,
    setShowLink,
    setShowMakeWebHook,
    setShowMedia,
    setShowNotifyMeByEmail,
    setShowOverrideVariable,
    setShowPassToAI,
    setShowReceiveAttachment,
    setShowRegisterUser,
    setShowSummarizeConversation,
    setShowSuperUserManagerServices,
    setShowSwitchToChatbot,
    setShowUserCredential,
    setShowVariable,
    closeForm,
  } = useMacBotActions(targetBlockToAddToId);

  const addBlockUiComponents = {
    START_POINT: {
      icon: <BiSolidHome className="tw-text-gray-800" size={22} />,
      label: 'Start Point',
      description: '',
      colors: {
        primary: '#9ca3af',
        secondary: '#D1D5DBA3',
        text: '#1f2937',
      },
      action: () => {
        setShowUserInput();
      },
      type: ENestedObjectType.Start,
    },

    ELSE: {
      icon: <FaMapSigns className="tw-text-gray-800" size={22} />,
      label: 'ELSE',
      description: '',
      colors: {
        primary: '#9ca3af',
        secondary: '#D1D5DBA3',
        text: '#1f2937',
      },
      action: () => {
        setShowElse();
      },
      type: ENestedObjectType.Else,
    },

    BOT_MESSAGE: {
      icon: <BiSolidBot className="tw-text-gray-800" size={22} />,
      label: t('AddItemModal/key2'),
      description: t('AddItemModal/desc2'),
      colors: {
        primary: '#93c5fd',
        secondary: '#93c5fd',
        text: '#1f2937',
      },
      action: setShowResponse,
      type: ENestedObjectType.Response,
    },

    SEND_MEDIA: {
      icon: <BiSolidImageAlt className="tw-text-gray-800" size={22} />,
      label: t('AddItemModal/key3'),
      description: t('AddItemModal/desc3'),
      colors: {
        primary: '#93c5fd',
        secondary: '#93c5fd',
        text: '#1f2937',
      },
      action: setShowMedia,
      type: ENestedObjectType.Media,
    },

    SEND_ATTACHMENT: {
      icon: <BiPaperclip className="tw-text-gray-800" size={22} />,
      label: t('AddItemModal/key4'),
      description: t('AddItemModal/desc4'),
      colors: {
        primary: '#93c5fd',
        secondary: '#93c5fd',
        text: '#1f2937',
      },
      action: setShowAttachment,
      type: ENestedObjectType.Attachment,
    },
    RECEIVE_ATTACHMENT: {
      icon: <BiFile className="tw-text-gray-800" size={22} />,
      label: t('MacReceiveFile/key1'),
      description: t('MacReceiveFile/desc1'),
      colors: {
        primary: '#93c5fd',
        secondary: '#93c5fd',
        text: '#1f2937',
      },
      action: setShowReceiveAttachment,
      type: ENestedObjectType.ReceiveAttachment,
    },
    ADD_BUTTON: {
      icon: <BiToggleLeft className="tw-text-gray-800" size={22} />,
      label: t('AddItemModal/key5'),
      description: t('AddItemModal/desc5'),
      colors: {
        primary: '#67e8f9',
        secondary: '#67e8f9',
        text: '#1f2937',
      },
      action: setShowButton,
      type: ENestedObjectType.Button,
    },

    ADD_LINK: {
      icon: <BiLinkExternal className="tw-text-gray-800" size={22} />,
      label: t('AddItemModal/key6'),
      description: t('AddItemModal/desc6'),
      colors: {
        primary: '#67e8f9',
        secondary: '#67e8f9',
        text: '#1f2937',
      },
      action: setShowLink,
      type: ENestedObjectType.TextLink,
    },

    CREATE_VARIABLE: {
      icon: <VariableIcon size={22} />,
      label: t('AddItemModal/key7'),
      description: t('AddItemModal/desc7'),
      colors: {
        primary: '#6ee7b7',
        secondary: '#6ee7b7',
        text: '#1f2937',
      },
      action: setShowCreateVariable,
      type: ENestedObjectType.CreateVariable,
    },

    ASSIGN_VARIABLE: {
      icon: <BiSolidShapes className="tw-text-gray-800" size={22} />,
      label: t('AddItemModal/key8'),
      description: t('AddItemModal/desc8'),
      colors: {
        primary: '#6ee7b7',
        secondary: '#6ee7b7',
        text: '#1f2937',
      },
      action: setShowVariable,
      type: ENestedObjectType.Variable,
    },

    OVERRIDE_VARIABLE: {
      icon: <BiArrowToRight className="tw-text-gray-800" size={22} />,
      label: t('AddItemModal/key9'),
      description: t('AddItemModal/desc9'),
      colors: {
        primary: '#6ee7b7',
        secondary: '#6ee7b7',
        text: '#1f2937',
      },
      action: setShowOverrideVariable,
      type: ENestedObjectType.OverrideVariable,
    },
    CONDITIONS: {
      icon: <BiShuffle className="tw-text-gray-800" size={22} />,
      label: t('AddItemModal/key11'),
      description: t('AddItemModal/desc11'),
      colors: {
        primary: '#6ee7b7',
        secondary: '#6ee7b7',
        text: '#1f2937',
      },
      action: setShowCondition,
      type: ENestedObjectType.Condition,
    },
    CONDITION_ELSE: {
      icon: <BiShuffle className="tw-text-gray-800" size={22} />,
      label: t('AddItemModal/key11'),
      description: t('AddItemModal/desc11'),
      colors: {
        primary: '#6ee7b7',
        secondary: '#6ee7b7',
        text: '#1f2937',
      },
      action: setShowElse,
      type: ENestedObjectType.ConditionElse,
    },
    JS_CODE: {
      icon: <BiLogoJavascript className="tw-text-gray-800" size={22} />,
      label: t('JSCode/Title'),
      description: t('JSCode/Description'),
      colors: {
        primary: '#fdba74',
        secondary: '#fdba74',
        text: '#1f2937',
      },
      action: setShowJSCode,
      type: ENestedObjectType.JSCode,
    },
    HTTP_REQUEST: {
      icon: <BiSolidMegaphone className="tw-text-gray-800" size={22} />,
      label: t('AddItemModal/key10'),
      description: t('AddItemModal/desc10'),
      colors: {
        primary: '#fdba74',
        secondary: '#fdba74',
        text: '#1f2937',
      },
      action: setShowHTTPRequest,
      type: ENestedObjectType.Http,
    },
    MAKE_WEBHOOK: {
      icon: <MakeIcon />,
      label: t('MacMakeCom/key1'),
      description: t('MacMakeCom/desc1'),
      colors: {
        primary: '#fdba74',
        secondary: '#fdba74',
        text: '#1f2937',
      },
      action: setShowMakeWebHook,
      type: ENestedObjectType.MakeWebHook,
    },
    SUPER_USER_MANAGER_SERVICES: {
      icon: <SuperUserManagerIcon />,
      label: t('SuperUserManager/key1'),
      description: t('SuperUserManager/key2'),
      colors: {
        primary: '#fdba74',
        secondary: '#fdba74',
        text: '#1f2937',
      },
      action: setShowSuperUserManagerServices,
      type: ENestedObjectType.SuperUserManagerServices,
    },
    NOTIFY_ME_BY_EMAIL: {
      icon: <BiAt size={22} />,
      label: t('MacNotifyEmail/key1'),
      description: t('MacNotifyEmail/desc1'),
      colors: {
        primary: '#fdba74',
        secondary: '#fdba74',
        text: '#1f2937',
      },
      action: setShowNotifyMeByEmail,
      type: ENestedObjectType.NotifyMeByEmail,
    },
    READ_USER_MESSAGE: {
      icon: <BiSolidUserVoice className="tw-text-gray-800" size={22} />,
      label: t('AddItemModal/key12'),
      description: t('AddItemModal/desc12'),
      colors: {
        primary: '#9e9d9d',
        secondary: '#fdba74',
        text: '#1f2937',
      },
      action: setShowUserInput,
      type: ENestedObjectType.UserInput,
    },
    SPEECH_RECORDING: {
      icon: <BiMicrophone className="tw-text-gray-800" size={22} />,
      label: t('AddItemModal/key18'),
      description: t('AddItemModal/desc18'),
      colors: {
        primary: '#9e9d9d',
        secondary: '#fdba74',
        text: '#1f2937',
      },
      action: setShowSpeechRecording,
      type: ENestedObjectType.SpeechRecording,
    },
    LEAD_FORM: {
      icon: <BiSolidSpreadsheet className="tw-text-gray-800" size={22} />,
      label: t('AddItemModal/key13'),
      description: t('AddItemModal/desc13'),
      colors: {
        primary: '#c4b5fd',
        secondary: '#c4b5fd',
        text: '#1f2937',
      },
      action: setShowLead,
      type: ENestedObjectType.Lead,
    },
    PASS_TO_AI: {
      icon: <BiBrain size={22} />,
      label: t('PassToAI/Title'),
      description: t('PassToAI/Description'),
      colors: {
        primary: '#a49ad6',
        secondary: '#c5bde1',
        text: '#1f2937',
      },
      action: setShowPassToAI,
      type: ENestedObjectType.PassToAI,
    },
    ASK_TO_AI: {
      icon: <BiMessageRoundedDots size={22} />,
      label: t('AskToAI/Title'),
      description: t('AskToAI/Description'),
      colors: {
        primary: '#a49ad6',
        secondary: '#c5bde1',
        text: '#1f2937',
      },
      action: setShowAskToAI,
      type: ENestedObjectType.AskToAI,
    },
    SWITCH_TO_CHATBOT: {
      icon: <BiFastForwardCircle size={22} />,
      label: t('SwitchToChatbot/Title'),
      description: t('SwitchToChatbot/Description'),
      colors: {
        primary: '#a49ad6',
        secondary: '#c5bde1',
        text: '#1f2937',
      },
      action: setShowSwitchToChatbot,
      type: ENestedObjectType.SwitchToChatbot,
    },
    SUMMARIZE_CONVERSATION: {
      icon: <MdCompress size={22} />,
      label: t('SummarizeConversation/Title'),
      description: t('SummarizeConversation/Description'),
      colors: {
        primary: '#a49ad6',
        secondary: '#c5bde1',
        text: '#1f2937',
      },
      action: setShowSummarizeConversation,
      type: ENestedObjectType.SummarizeConversation,
    },
    DELAY: {
      icon: <BiSolidTimeFive size={22} />,
      label: t('AddItemModal/key16'),
      description: t('AddItemModal/desc16'),
      colors: {
        primary: '#fcd34d',
        secondary: '#fcd34d',
        text: '#1f2937',
      },
      action: setShowDelay,
      type: ENestedObjectType.Delay,
    },

    GO_TO: {
      icon: <BiUndo className="tw-text-gray-800" size={22} />,
      label: t('AddItemModal/key14'),
      description: t('AddItemModal/desc14'),
      colors: {
        primary: '#fda4af',
        secondary: '#fda4af',
        text: '#1f2937',
      },
      action: setShowGoto,
      type: ENestedObjectType.Goto,
    },

    END_OF_CONVERSATION: {
      icon: <BiSolidExit size={22} />,
      label: t('AddItemModal/key15'),
      description: t('AddItemModal/desc15'),
      colors: {
        primary: '#fda4af',
        secondary: '#fda4af',
        text: '#1f2937',
      },
      action: setShowEndConversation,
      type: ENestedObjectType.EndOfConversation,
    },

    REGISTER_USER: {
      icon: <BiUserPlus size={22} />,
      label: t('SuperUserManager/key3'),
      description: t('SuperUserManager/key4'),
      colors: {
        primary: '#fdba74',
        secondary: '#fdba74',
        text: '#1f2937',
      },
      action: setShowRegisterUser,
      type: ENestedObjectType.RegisterUser,
    },
    GET_USER_INFO: {
      icon: <BiUser size={22} />,
      label: t('SuperUserManager/key9'),
      description: t('SuperUserManager/key10'),
      colors: {
        primary: '#fdba74',
        secondary: '#fdba74',
        text: '#1f2937',
      },
      action: setShowGetUserInfo,
      type: ENestedObjectType.GetUserProfile,
    },
    CHECK_IF_USER_EXISTS: {
      icon: <BiUserCheck size={22} />,
      label: t('SuperUserManager/key7'),
      description: t('SuperUserManager/key8'),
      colors: {
        primary: '#fdba74',
        secondary: '#fdba74',
        text: '#1f2937',
      },
      action: setShowCheckIfUserExists,
      type: ENestedObjectType.CheckIfUserExists,
    },
    USER_CREDENTIAL: {
      icon: <BiLogInCircle size={22} />,
      label: t('SuperUserManager/key5'),
      description: t('SuperUserManager/key6'),
      colors: {
        primary: '#fdba74',
        secondary: '#fdba74',
        text: '#1f2937',
      },
      action: setShowUserCredential,
      type: ENestedObjectType.UserLoginForm,
    },
  };

  const scenarioMenuFilter = () => {
    const keysToRemove = new Set(['USER_CREDENTIAL', 'CHECK_IF_USER_EXISTS', 'GET_USER_INFO', 'REGISTER_USER']);

    // Filter out unwanted keys from addBlockUiComponents
    const filteredComponents = Object.entries(addBlockUiComponents)
      .filter(([key]) => !keysToRemove.has(key))
      .map(([name, component]) => ({ ...component, name }));

    // Filter components by acceptableBlocksToAdd list
    const acceptableBlocks = filteredComponents.filter(({ name }) => acceptableBlocksToAdd.includes(name as AcceptableItemsToAddType));

    // Find the target node in treeData
    const targetNode = treeData.find(({ id }) => id === targetBlockToAddToId);
    const firstChild = targetNode?.children?.[0];

    if (!firstChild) return acceptableBlocks;

    // Get features for the first child node
    const feature: any = getFeatures(firstChild.type, { title: firstChild.title, block_id: firstChild.id }, null);

    const firstChildFeatureName = feature[firstChild.type]?.name;

    // Filter acceptable blocks based on feature compatibility
    return acceptableBlocks.filter(block => {
      const blockFeature = feature[block.type];

      if (blockFeature?.acceptableBlocks && firstChildFeatureName) {
        if (blockFeature.acceptableBlocks.includes(firstChildFeatureName)) {
          return true;
        } else if (blockFeature.sibling && blockFeature.sibling.includes(firstChildFeatureName)) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    });
  };

  const useRefIframe = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    // Set up event listener for messages from the iframe
    const handleMessage = (event: any) => {
      // Ensure the message is coming from the expected origin
      if (event.origin == `${import.meta.env.VITE_CHATBOT_WIDGET}` && event?.data == 'CloseLivePreview') {
        setIsLivePreview(false);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(() => {
    if (isMobile || window.screen.width <= 480) {
      setIsLivePreview(false);
    }
  }, [isMobile]);

  const { isLoading, isError, isSuccess, isFetching, data: getScenarioDetailData, refetch } = useGetScenarioDetailQuery(`${currentScenarioId}`);

  useEffect(() => {
    closeForm();
    dispatch(setIsFloatSideMenuShow(false));
    dispatch(setSideMenuSize('sm'));

    const showModal = localStorage.getItem('VSBuilderMobileModal');
    if (showModal && JSON.parse(showModal) === client?.sum_identification) setShowMobileModal(false);
    else setShowMobileModal(true);
  }, []);

  const [isLoadingPage, setIsLoadingPage] = useState(true);

  const [modifyScenario, modifyScenarioStatus] = useModifyScenarioMutation();

  const [publishScenario] = usePublishScenariosMutation();

  useEffect(() => {
    const handleContextmenu = (e: any) => {
      e.preventDefault();
    };
    document.addEventListener('contextmenu', handleContextmenu);
    return function cleanup() {
      document.removeEventListener('contextmenu', handleContextmenu);
    };
  }, []);

  useEffect(() => {
    if (isSuccess) {
      setIsLoadingPage(isLoading);
    }
  }, [isLoading, isSuccess]);

  const [data, setData] = useState<INestedObject>({
    id: 'Start',
    title: 'Start Point',
    type: ENestedObjectType.Start,
    children: [],
  });

  useEffect(() => {
    setTimeout(() => {
      document.getElementById('btn-home')?.click();
    }, 1000);
  }, []);

  const { treeRef } = useTree();

  const click = (data: any, type: ENestedObjectType) => {
    const map: Record<ENestedObjectType, (data?: any) => void> = {
      [ENestedObjectType.Response]: setShowResponse,
      [ENestedObjectType.Button]: setShowButton,
      [ENestedObjectType.NotifyMeByEmail]: setShowNotifyMeByEmail,
      [ENestedObjectType.Else]: setShowElse,
      [ENestedObjectType.JSCode]: setShowJSCode,
      [ENestedObjectType.UserInput]: setShowUserInput,
      [ENestedObjectType.TextLink]: setShowLink,
      [ENestedObjectType.Variable]: setShowVariable,
      [ENestedObjectType.Http]: setShowHTTPRequest,
      [ENestedObjectType.MakeWebHook]: setShowMakeWebHook,
      [ENestedObjectType.Condition]: setShowCondition,
      [ENestedObjectType.OverrideVariable]: setShowOverrideVariable,
      [ENestedObjectType.SpeechRecording]: setShowSpeechRecording,
      [ENestedObjectType.CreateVariable]: setShowCreateVariable,
      [ENestedObjectType.SummarizeConversation]: setShowSummarizeConversation,
      [ENestedObjectType.Lead]: setShowLead,
      [ENestedObjectType.Media]: setShowMedia,
      [ENestedObjectType.Attachment]: setShowAttachment,
      [ENestedObjectType.EndOfConversation]: setShowEndConversation,
      [ENestedObjectType.ReceiveAttachment]: setShowReceiveAttachment,
      [ENestedObjectType.Delay]: setShowDelay,
      [ENestedObjectType.Goto]: setShowGoto,
      [ENestedObjectType.SuperUserManagerServices]: setShowSuperUserManagerServices,
      [ENestedObjectType.GetUserProfile]: setShowGetUserInfo,
      [ENestedObjectType.RegisterUser]: setShowRegisterUser,
      [ENestedObjectType.CheckIfUserExists]: setShowCheckIfUserExists,
      [ENestedObjectType.UserLoginForm]: setShowUserCredential,
      [ENestedObjectType.PassToAI]: setShowPassToAI,
      [ENestedObjectType.SwitchToChatbot]: setShowSwitchToChatbot,
      [ENestedObjectType.AskToAI]: setShowAskToAI,
      [ENestedObjectType.VideoBlock]: function (data?: any): void {
        throw new Error('Function not implemented.');
      },
      [ENestedObjectType.VideoGoto]: function (data?: any): void {
        throw new Error('Function not implemented.');
      },
      [ENestedObjectType.VideoButton]: function (data?: any): void {
        throw new Error('Function not implemented.');
      },
      [ENestedObjectType.VideoLink]: function (data?: any): void {
        throw new Error('Function not implemented.');
      },
      [ENestedObjectType.ConditionElse]: function (data?: any): void {
        throw new Error('Function not implemented.');
      },
      [ENestedObjectType.Start]: function (data?: any): void {
        throw new Error('Function not implemented.');
      },
    };

    const fn = map[type] || closeForm;
    fn(data);
  };

  const ResetLivePreview = () => {
    const iframe = useRefIframe.current;

    iframe?.contentWindow?.postMessage('ResetLivePreview', `${import.meta.env.VITE_CHATBOT_WIDGET}`);
  };

  const hasTrueShowKey = (object: Record<string, any>) => {
    for (const key in object) {
      if (key.startsWith('isShow') && object[key] === true) {
        return true;
      }
    }
    return false;
  };

  const getFeatures = (type: ENestedObjectType, rest: any, item: any) => {
    return {
      [ENestedObjectType.Start]: {
        name: 'START_POINT',
        id: 'STARTBUTTON',
        icon: addBlockUiComponents['START_POINT'].icon,
        acceptableBlocks: ['BOT_MESSAGE'],
        label: t('KnowledgeBasesCreateConditionalScenario/text-05'),
        colors: addBlockUiComponents['START_POINT'].colors,
      },
      [ENestedObjectType.Response]: {
        name: 'BOT_MESSAGE',
        id: '',
        icon: addBlockUiComponents['BOT_MESSAGE'].icon,
        acceptableBlocks: acceptableBlocksMap[ENestedObjectType.Response],
        label: (
          <>
            <span className="tw-text-primary">{rest.block_id}</span> <span title={rest.title}>{rest.title}</span>
          </>
        ),
        colors: addBlockUiComponents['BOT_MESSAGE'].colors,
      },
      [ENestedObjectType.SummarizeConversation]: {
        name: 'SUMMARIZE_CONVERSATION',
        id: '',
        icon: addBlockUiComponents['SUMMARIZE_CONVERSATION'].icon,
        acceptableBlocks: acceptableBlocksMap[ENestedObjectType.SummarizeConversation],
        label: (
          <>
            <span className="tw-text-primary">{rest.block_id}</span> <span title={rest.title}>{rest.title}</span>
          </>
        ),
        colors: addBlockUiComponents['SUMMARIZE_CONVERSATION'].colors,
      },
      [ENestedObjectType.PassToAI]: {
        name: 'PASS_TO_AI',
        id: '',
        icon: addBlockUiComponents['PASS_TO_AI'].icon,
        acceptableBlocks: acceptableBlocksMap[ENestedObjectType.PassToAI],
        label: (
          <>
            <span className="tw-text-primary">{rest.block_id}</span> <span title={rest.title}>{rest.title}</span>
          </>
        ),
        colors: addBlockUiComponents['PASS_TO_AI'].colors,
      },
      [ENestedObjectType.AskToAI]: {
        name: 'ASK_TO_AI',
        id: '',
        icon: addBlockUiComponents['ASK_TO_AI'].icon,
        acceptableBlocks: acceptableBlocksMap[ENestedObjectType.AskToAI],
        label: (
          <>
            <span className="tw-text-primary">{rest.block_id}</span> <span title={rest.title}>{rest.title}</span>
          </>
        ),
        colors: addBlockUiComponents['ASK_TO_AI'].colors,
      },
      [ENestedObjectType.SwitchToChatbot]: {
        name: 'SWITCH_TO_CHATBOT',
        id: '',
        icon: addBlockUiComponents['SWITCH_TO_CHATBOT'].icon,
        acceptableBlocks: acceptableBlocksMap[ENestedObjectType.SwitchToChatbot],
        label: (
          <>
            <span className="tw-text-primary">{rest.block_id}</span> <span title={rest.title}>{rest.title}</span>
          </>
        ),
        colors: addBlockUiComponents['SWITCH_TO_CHATBOT'].colors,
      },
      [ENestedObjectType.NotifyMeByEmail]: {
        name: 'NOTIFY_ME_BY_EMAIL',
        id: '',
        icon: addBlockUiComponents['NOTIFY_ME_BY_EMAIL'].icon,
        acceptableBlocks: acceptableBlocksMap[ENestedObjectType.NotifyMeByEmail],
        label: (
          <>
            <span className="tw-text-primary">{rest.block_id}</span> <span title={rest.title}>{rest.title}</span>
          </>
        ),
        colors: addBlockUiComponents['NOTIFY_ME_BY_EMAIL'].colors,
      },
      [ENestedObjectType.ReceiveAttachment]: {
        name: 'RECEIVE_ATTACHMENT',
        id: '',
        icon: addBlockUiComponents['RECEIVE_ATTACHMENT'].icon,
        acceptableBlocks: acceptableBlocksMap[ENestedObjectType.ReceiveAttachment],
        label: (
          <>
            <span className="tw-text-primary">{rest.block_id}</span> <span title={rest.title}>{rest.title}</span>
          </>
        ),
        colors: addBlockUiComponents['RECEIVE_ATTACHMENT'].colors,
      },
      [ENestedObjectType.SpeechRecording]: {
        name: 'SPEECH_RECORDING',
        id: '',
        icon: addBlockUiComponents['SPEECH_RECORDING'].icon,
        acceptableBlocks: acceptableBlocksMap[ENestedObjectType.SpeechRecording],
        label: (
          <>
            <span className="tw-text-primary">{rest.block_id}</span> <span title={rest.title}>{rest.title}</span>
          </>
        ),
        colors: addBlockUiComponents['SPEECH_RECORDING'].colors,
      },
      [ENestedObjectType.Else]: {
        name: 'ELSE',
        id: '',
        icon: addBlockUiComponents['READ_USER_MESSAGE'].icon,
        acceptableBlocks: acceptableBlocksMap[ENestedObjectType.Else],
        label: (
          <>
            <span className="tw-text-primary">{rest.block_id}</span> <span>{rest.title ?? ''}</span>
          </>
        ),
        colors: addBlockUiComponents['READ_USER_MESSAGE'].colors,
      },
      [ENestedObjectType.UserInput]: {
        name: 'READ_USER_MESSAGE',
        id: '',
        icon: addBlockUiComponents['READ_USER_MESSAGE'].icon,
        acceptableBlocks: acceptableBlocksMap[ENestedObjectType.UserInput],
        label: (
          <div className="tw-flex tw-items-center">
            <span className="tw-text-primary tw-mr-2">{rest.block_id}</span>
            {(rest?.keywords as string[])?.length > 2 ? (
              <React.Fragment>
                {(rest?.keywords as string[]).slice(0, 2).map((dr: string, index: number) => (
                  <div key={index} className="tw-bg-gray-200 tw-px-2 tw-py-[1px] tw-font-normal tw-text-zinc-900 tw-rounded-xl tw-text-xs font-sofiaSans">
                    {dr?.length > 5 ? dr.slice(0, 5) + '...' : dr}
                  </div>
                ))}

                <div className="tw-bg-gray-200 tw-px-2 tw-py-[1px] tw-font-normal tw-text-zinc-900 tw-rounded-xl tw-text-xs font-sofiaSans">
                  +{(rest?.keywords as string[]).length - 2} more
                </div>
              </React.Fragment>
            ) : (
              <React.Fragment>
                {rest?.keywords?.map((dr: string, index: number) => (
                  <div key={index} className="tw-bg-gray-200 tw-px-2 tw-py-[1px] tw-font-normal tw-text-zinc-900 tw-rounded-xl tw-text-xs font-sofiaSans">
                    {dr?.length > 5 ? dr.slice(0, 5) + '...' : dr}
                  </div>
                ))}
              </React.Fragment>
            )}
          </div>
        ),
        colors: addBlockUiComponents['READ_USER_MESSAGE'].colors,
        sibling: ['READ_USER_MESSAGE', 'ELSE'],
      },
      [ENestedObjectType.Variable]: {
        name: 'ASSIGN_VARIABLE',
        id: '',
        icon: addBlockUiComponents['ASSIGN_VARIABLE'].icon,
        acceptableBlocks: acceptableBlocksMap[ENestedObjectType.Variable],
        label: (
          <>
            <span className="tw-text-primary">{rest.block_id}</span> {rest.title ?? ''}
          </>
        ),
        colors: addBlockUiComponents['ASSIGN_VARIABLE'].colors,
      },
      [ENestedObjectType.Goto]: {
        name: 'GO_TO',
        id: '',
        icon: addBlockUiComponents['GO_TO'].icon,
        acceptableBlocks: acceptableBlocksMap[ENestedObjectType.Goto],
        label: (
          <>
            <span className="tw-text-primary">{treeData?.find((dr: any) => dr.id == item?.data?.next)?.block_id}</span>{' '}
            {treeData?.find((dr: any) => dr.id == item?.data?.next)?.title?.length > 20
              ? treeData?.find((dr: any) => dr.id == item?.data?.next)?.title?.slice(0, 20) + '...'
              : treeData?.find((dr: any) => dr.id == item?.data?.next)?.title}
          </>
        ),
        colors: addBlockUiComponents['GO_TO'].colors,
      },
      [ENestedObjectType.Button]: {
        name: 'ADD_BUTTON',
        id: '',
        icon: addBlockUiComponents['ADD_BUTTON'].icon,
        acceptableBlocks: acceptableBlocksMap[ENestedObjectType.Button],
        label: (
          <>
            <span className="tw-text-primary">{rest.block_id}</span> {rest.title ?? ''}
          </>
        ),
        colors: addBlockUiComponents['ADD_BUTTON'].colors,
        sibling: ['ADD_BUTTON', 'ADD_LINK'],
      },
      [ENestedObjectType.TextLink]: {
        name: 'ADD_LINK',
        id: '',
        icon: addBlockUiComponents['ADD_LINK'].icon,
        acceptableBlocks: acceptableBlocksMap[ENestedObjectType.TextLink],
        label: (
          <>
            <span className="tw-text-primary">{rest.block_id}</span> {rest.title ?? ''}
          </>
        ),
        colors: addBlockUiComponents['ADD_LINK'].colors,
        sibling: ['ADD_BUTTON', 'ADD_LINK'],
      },
      [ENestedObjectType.JSCode]: {
        name: 'JS_CODE',
        id: '',
        icon: addBlockUiComponents['JS_CODE'].icon,
        acceptableBlocks: acceptableBlocksMap[ENestedObjectType.JSCode],
        label: (
          <>
            <span className="tw-text-primary">{rest.block_id}</span> {rest.title ?? ''}
          </>
        ),
        colors: addBlockUiComponents['JS_CODE'].colors,
      },
      [ENestedObjectType.Http]: {
        name: 'HTTP_REQUEST',
        id: '',
        icon: addBlockUiComponents['HTTP_REQUEST'].icon,
        acceptableBlocks: acceptableBlocksMap[ENestedObjectType.Http],
        label: (
          <>
            <span className="tw-text-primary">{rest.block_id}</span> {rest.title ?? ''}
          </>
        ),
        colors: addBlockUiComponents['HTTP_REQUEST'].colors,
      },
      [ENestedObjectType.MakeWebHook]: {
        name: 'MAKE_WEBHOOK',
        id: '',
        icon: addBlockUiComponents['MAKE_WEBHOOK'].icon,
        acceptableBlocks: acceptableBlocksMap[ENestedObjectType.MakeWebHook],
        label: (
          <>
            <span className="tw-text-primary">{rest.block_id}</span> {rest.title ?? ''}
          </>
        ),
        colors: addBlockUiComponents['MAKE_WEBHOOK'].colors,
      },
      [ENestedObjectType.Condition]: {
        name: 'CONDITIONS',
        id: '',
        icon: addBlockUiComponents['CONDITIONS'].icon,
        acceptableBlocks: acceptableBlocksMap[ENestedObjectType.Condition],
        label: (
          <>
            <span className="tw-text-primary">{rest.block_id}</span> {rest.title ?? ''}
          </>
        ),
        colors: addBlockUiComponents['CONDITIONS'].colors,
        sibling: ['CONDITIONS', 'CONDITION_ELSE'],
      },
      [ENestedObjectType.ConditionElse]: {
        name: 'CONDITION_ELSE',
        id: '',
        icon: addBlockUiComponents['CONDITION_ELSE'].icon,
        acceptableBlocks: acceptableBlocksMap[ENestedObjectType.ConditionElse],
        label: (
          <>
            <span className="tw-text-primary">{rest.block_id}</span> {rest.title ?? ''}
          </>
        ),
        colors: addBlockUiComponents['CONDITION_ELSE'].colors,
        sibling: ['CONDITIONS', 'CONDITION_ELSE'],
      },
      [ENestedObjectType.Delay]: {
        name: 'DELAY',
        id: '',
        icon: addBlockUiComponents['DELAY'].icon,
        acceptableBlocks: acceptableBlocksMap[ENestedObjectType.Delay],
        label: (
          <>
            <span className="tw-text-primary">{rest.block_id}</span> {rest?.delay ? rest.delay + ' Sec' : ''}
          </>
        ),
        colors: addBlockUiComponents['DELAY'].colors,
      },
      [ENestedObjectType.EndOfConversation]: {
        name: 'END_OF_CONVERSATION',
        id: '',
        icon: addBlockUiComponents['END_OF_CONVERSATION'].icon,
        acceptableBlocks: acceptableBlocksMap[ENestedObjectType.EndOfConversation],
        label: (
          <>
            <span className="tw-text-primary">{rest.block_id}</span> {rest.title ?? ''}
          </>
        ),
        colors: addBlockUiComponents['END_OF_CONVERSATION'].colors,
      },
      [ENestedObjectType.OverrideVariable]: {
        name: 'OVERRIDE_VARIABLE',
        id: '',
        icon: addBlockUiComponents['OVERRIDE_VARIABLE'].icon,
        acceptableBlocks: acceptableBlocksMap[ENestedObjectType.OverrideVariable],
        label: (
          <>
            <span className="tw-text-primary">{rest.block_id}</span> {rest.title ?? ''}
          </>
        ),
        colors: addBlockUiComponents['OVERRIDE_VARIABLE'].colors,
      },
      [ENestedObjectType.CreateVariable]: {
        name: 'CREATE_VARIABLE',
        id: '',
        icon: addBlockUiComponents['CREATE_VARIABLE'].icon,
        acceptableBlocks: acceptableBlocksMap[ENestedObjectType.CreateVariable],
        label: (
          <>
            <span className="tw-text-primary">{rest.block_id}</span> {rest.title ?? ''}
          </>
        ),
        colors: addBlockUiComponents['CREATE_VARIABLE'].colors,
      },
      [ENestedObjectType.Lead]: {
        name: 'LEAD_FORM',
        id: '',
        icon: addBlockUiComponents['LEAD_FORM'].icon,
        acceptableBlocks: acceptableBlocksMap[ENestedObjectType.Lead],
        label: (
          <>
            <span className="tw-text-primary">{rest.block_id}</span> {rest.title ?? ''}
          </>
        ),
        colors: addBlockUiComponents['LEAD_FORM'].colors,
      },
      [ENestedObjectType.Media]: {
        name: 'SEND_MEDIA',
        id: '',
        icon: addBlockUiComponents['SEND_MEDIA'].icon,
        acceptableBlocks: acceptableBlocksMap[ENestedObjectType.Media],
        label: (
          <>
            <span className="tw-text-primary">{rest.block_id}</span> {rest.title ?? ''}
          </>
        ),
        colors: addBlockUiComponents['SEND_MEDIA'].colors,
      },
      [ENestedObjectType.Attachment]: {
        name: 'SEND_ATTACHMENT',
        id: '',
        icon: addBlockUiComponents['SEND_ATTACHMENT'].icon,
        acceptableBlocks: acceptableBlocksMap[ENestedObjectType.Attachment],
        label: (
          <>
            <span className="tw-text-primary">{rest.block_id}</span> {rest.title ?? ''}
          </>
        ),
        colors: addBlockUiComponents['SEND_ATTACHMENT'].colors,
      },
      [ENestedObjectType.SuperUserManagerServices]: {
        name: 'SUPER_USER_MANAGER_SERVICES',
        id: '',
        icon: addBlockUiComponents['SUPER_USER_MANAGER_SERVICES'].icon,
        acceptableBlocks: acceptableBlocksMap[ENestedObjectType.SuperUserManagerServices],
        label: (
          <>
            <span className="tw-text-primary">{rest.block_id}</span> {rest.title ?? ''}
          </>
        ),
        colors: addBlockUiComponents['SUPER_USER_MANAGER_SERVICES'].colors,
      },
      [ENestedObjectType.RegisterUser]: {
        name: 'REGISTER_USER',
        id: '',
        icon: addBlockUiComponents['REGISTER_USER'].icon,
        acceptableBlocks: acceptableBlocksMap[ENestedObjectType.RegisterUser],
        label: (
          <>
            <span className="tw-text-primary">{rest.block_id}</span> {rest.title ?? ''}
          </>
        ),
        colors: addBlockUiComponents['REGISTER_USER'].colors,
      },
      [ENestedObjectType.GetUserProfile]: {
        name: 'GET_USER_INFO',
        id: '',
        icon: addBlockUiComponents['GET_USER_INFO'].icon,
        acceptableBlocks: acceptableBlocksMap[ENestedObjectType.GetUserProfile],
        label: (
          <>
            <span className="tw-text-primary">{rest.block_id}</span> {rest.title ?? ''}
          </>
        ),
        colors: addBlockUiComponents['GET_USER_INFO'].colors,
      },
      [ENestedObjectType.CheckIfUserExists]: {
        name: 'CHECK_IF_USER_EXISTS',
        id: '',
        icon: addBlockUiComponents['CHECK_IF_USER_EXISTS'].icon,
        acceptableBlocks: acceptableBlocksMap[ENestedObjectType.CheckIfUserExists],
        label: (
          <>
            <span className="tw-text-primary">{rest.block_id}</span> {rest.title ?? ''}
          </>
        ),
        colors: addBlockUiComponents['CHECK_IF_USER_EXISTS'].colors,
      },
      [ENestedObjectType.UserLoginForm]: {
        name: 'USER_CREDENTIAL',
        id: '',
        icon: addBlockUiComponents['USER_CREDENTIAL'].icon,
        acceptableBlocks: acceptableBlocksMap[ENestedObjectType.UserLoginForm],
        label: (
          <>
            <span className="tw-text-primary">{rest.block_id}</span> {rest.title ?? ''}
          </>
        ),
        colors: addBlockUiComponents['USER_CREDENTIAL'].colors,
      },
    };
  };

  const renderCard = (item: IRenderCard) => {
    const {
      data: { type, id, notes, ...rest },
    } = item;

    const features: any = getFeatures(type as ENestedObjectType, rest, item);

    if (typeof type === 'undefined') return null;

    const excludedBlockTypes = [BlockType.EndOFConversation, BlockType.Goto, BlockType.Start, BlockType.PassToAi, BlockType.SwitchToChatbot];

    return (
      <TreeNode
        key={id}
        originalRenderData={item}
        data={features[+type]}
        onClickToEdit={(data, type) => {
          closeForm(macBotState.parentId);

          setIsLivePreview(false);
          setShowPlusModal(false);

          click(data, type);
        }}
        isActive={id === macBotState.parentId && hasTrueShowKey(macBotState)}
        notes={notes}
      >
        {!excludedBlockTypes.includes(+type) && (
          <button
            id="btn-plus-text-scenario"
            onClick={() => {
              closeForm(macBotState.parentId);

              setShowPlusModal(true);
              setTargetBlockToAddToId(id as string);
              setAcceptableBlocksToAdd(features[+type].acceptableBlocks);
              setIsLivePreview(false);
            }}
            type="button"
            className="tw-border-none tw-bg-transparent tw-p-0 tw-flex tw-items-center"
          >
            <div className="tw-w-8 tw-h-[5px] tw-bg-[#C1C8CD] tw-relative tw-top-[-1px]" />
            <div
              className={`tw-rounded-full tw-shrink-0 tw-flex-tw-flex-col tw-items-center tw-justify-center ${
                showPlusModal && targetBlockToAddToId === id
                  ? 'tw-bg-white tw-border-solid tw-border-4 tw-border-blue-600 tw-p-[0.4px] tw-w-8 tw-h-8'
                  : type !== 20 && type !== 3 && item?.data?.childrens?.length <= 0
                  ? 'tw-bg-white tw-border-solid tw-border-4 tw-border-red-600 tw-p-[0.4px] tw-w-8 tw-h-8 blink'
                  : 'tw-border-none'
              } `}
            >
              <BiSolidPlusCircle
                size={22}
                className={`tw-text-gray-400 tw-relative ${
                  (showMobileModal && targetBlockToAddToId === id) || (type !== 20 && type !== 3 && item?.data?.childrens?.length <= 0) ? 'tw-top-[-2px]' : ''
                }`}
              />
            </div>
          </button>
        )}
      </TreeNode>
    );
  };

  const renderButton = (item: IRenderButton) => {
    return <div></div>;
  };

  const setLastTree = (step: any) => {
    dispatch(addUndoRedoStep({ step }));
  };

  useEffect(() => {
    if (isSuccess && currentScenarioId && !isFetching) {
      if (getScenarioDetailData?.type == BotTypeEnum.VIDEO_SCENARIO) {
        return navigate(`?type=video-scenario`);
      }
      setDataToChart(getScenarioDetailData);
      if (getScenarioDetailData?.title) {
        dispatch(setCurrentScenarioTitle(getScenarioDetailData.title));
      }
    }
  }, [currentScenarioId, isSuccess, isFetching]);

  const setDataToChart = (dataNodes: any) => {
    ResetLivePreview();

    const temp = JSON.parse(JSON.stringify(data));

    const tempData = JSON.parse(JSON.stringify(dataNodes));

    if (tempData?.config?.tree) {
      const length = (tempData?.config?.tree as any[]).length;
      if (length > 50) {
        setScale(0.5);
      } else {
        setScale(0.8);
      }

      setTreeData([...tempData.config.tree]);

      const config = {
        ...tempData.config,
        metaData: {
          ...tempData.config.metaData,
          variables: tempData.config.metaData.variables?.filter((x: any) => !x.variableName.includes('_[PD]_')),
        },
      };

      setConfigData(config);
    }

    const response = createNestedStructure(tempData.config?.tree || []);

    temp.children = response;

    setData(temp);
  };

  function createNestedStructure(accounts: any[], parentId = null) {
    const result = [];

    for (const account of accounts) {
      if (account.parent === parentId) {
        const children = createNestedStructure(accounts, account.id);
        if (children.length > 0) {
          let childrenTemp = children?.sort((a, b) => a?.order_id - b?.order_id);

          if (childrenTemp.length > 1 && childrenTemp.find(dr => dr.type == 0)) {
            childrenTemp = childrenTemp.reverse();
          }

          const sampleData = [...childrenTemp];

          const elseBlock = sampleData.filter(item => [0, 14].includes(item.type));

          const noneElseBlock = sampleData.filter(item => ![0, 14].includes(item.type));

          const resultBlock = noneElseBlock.concat(elseBlock);

          account.children = resultBlock;
        }
        result.push(account);
      }
    }

    return result;
  }

  const editable = () => {
    setEditScenario;
    dispatch(
      setEditScenario({
        botService: createState.chatbotService,
        id: currentScenarioId,
        chatbotTitle: createState.chatbotTitle,
      } as unknown as IUpdateKnowledgeAssistant)
    );

    dispatch(setChatBotService(createState.chatbotService));
  };

  const createScenarioBlock = (block: any) => {
    return new Promise<void>((resolve, reject) => {
      editable();

      const data = {
        ...Utilities.setBlockNotes(block),
        block_id: Utilities.createBlockId(block.title, treeData),
      };

      // // setFetchGetScenarioDetail(true);
      modifyScenario({
        scenarioId: `${currentScenarioId}`,
        data: {
          single: {
            action: 'add',
            data: data,
          },
        },
      })
        .unwrap()
        .then(data => {
          toast.success(t('General/key10'));

          setLastTree({
            action: 'add',
            data: data?.config?.tree[data?.config?.tree?.length - 1],
          });

          if (block.type != ENestedObjectType.SuperUserManagerServices) {
            closeForm();
          }

          setDataToChart(data);

          setHasUnpublishedData(true);
          resolve(data);
        })
        .catch(res => {
          if (res.status < 500) {
            if (typeof res?.data?.element == 'object') {
              const messagesList = res?.data?.element?.map((el: any) => Object.entries(el.messages)?.map(([key, value]) => `${value}`)).flat();

              if (messagesList) {
                for (const message of messagesList) {
                  toast.warn(message);
                }
              } else {
                toast.warn(res?.data?.message || res.message);
              }
            }
          }

          if (res.status >= 500) toast.error(res?.data?.message || res.message);

          // let errorMessage = res?.data?.message || t(AlertsType.ISGLOK001);
          // if (res.status == 403) {
          //   errorMessage = t(AlertsType.ISGLOK001);
          // }
          // toast.error(errorMessage, { delay: 100 });
          reject(res);
        });
    });
  };

  const changeScenarioBlock = (block: any, isUpdateTree = true, isUndoRedoState = true) => {
    return new Promise<void>((resolve, reject) => {
      editable();
      // // setFetchGetScenarioDetail(true);
      const { children, ...dataNoChild } = Utilities.setBlockNotes(block);

      modifyScenario({
        scenarioId: `${currentScenarioId}`,
        data: {
          single: {
            action: 'change',
            data: dataNoChild,
          },
        },
      })
        .unwrap()
        .then(data => {
          toast.success(t('General/key11'));

          if (isUndoRedoState) {
            setLastTree({
              action: 'change',
              data: treeData.find((dr: any) => dr.id == block.id),
            });
          }

          closeForm();

          if (isUpdateTree) {
            setDataToChart(data);
          }
          setHasUnpublishedData(true);
          resolve(data);
        })
        .catch(res => {
          if (res.status < 500) {
            const messagesList = res?.data?.element?.map((el: any) => Object.entries(el.messages)?.map(([key, value]) => `${value}`)).flat();

            if (messagesList) {
              for (const message of messagesList) {
                toast.warn(message);
              }
            } else {
              toast.warn(res?.data?.message || res.message);
            }
          }

          if (res.status >= 500) toast.error(res?.data?.message || res.message);

          // let errorMessage = res?.data?.message || t(AlertsType.ISGLOK001);
          // if (res.status == 403) {
          //   errorMessage = t(AlertsType.ISGLOK001);
          // }
          // toast.error(errorMessage, { delay: 100 });
          reject(res);
        });
    });
  };

  const deleteScenarioBlock = (block: any, isUpdateTree = true, isUndoRedoState = true) => {
    return new Promise<void>((resolve, reject) => {
      // setFetchGetScenarioDetail(true);
      closeForm();

      let message = `${t('KnowledgeBasesCreateConditionalScenario/text-01')} "${
        (block.title as string)?.length > 10 ? (block.title as string).substring(0, 10) + '...' : (block.title as string)
      }"?`;

      if (block.type == BlockType.UserInput || block.type == BlockType.Goto || block.type == BlockType.SUMLogout) {
        message = `${t('KnowledgeBasesCreateConditionalScenario/text-03')}?`;
      }

      const showDescription =
        block.type === BlockType.Condition ||
        block.type == BlockType.UserInput ||
        block.type == BlockType.Button ||
        block.type == BlockType.TextLink ||
        block.type == BlockType.UserLoginForm ||
        block.type == BlockType.CheckUserExists ||
        block.type == BlockType.InjectTemplate ||
        treeData.find(node => node.id === block.parent)?.childrens.length > 1;
      confirmWithDialog(message, {
        // condition 13
        // use-input (tags) 2
        // or if currently selected block parent has more than one child

        description: showDescription ? t('KnowledgeBasesCreateConditionalScenario/text-04') : undefined,
        imageUrl: showDescription ? 'https://cdn.1cdn.app/application/LOUISEBOT/24040205081340_Image1.svg' : undefined,
      }).then((res: any) => {
        if (res) {
          modifyScenario({
            scenarioId: `${currentScenarioId}`,
            data: {
              single: {
                action: 'remove',
                data: {
                  type: block.type,
                  id: block.id,
                },
              },
            },
          })
            .unwrap()
            .then(data => {
              toast.success(t('General/key12'));

              editable();
              if (isUndoRedoState) {
                setLastTree({
                  action: 'remove',
                  data: block,
                });
              }
              dispatch(resetUndoRedoScenario());
              setDataToChart(data);

              setHasUnpublishedData(true);
              resolve(data);
            })
            .catch(res => {
              if (res.status < 500) toast.warn(res?.data?.message || res.message);

              if (res.status >= 500) toast.error(res?.data?.message || res.message);

              // if (res.status == 403) {
              //   toast.error(t(AlertsType.ISKBKO014), {
              //     delay: 100
              //   });
              // } else {
              //   toast.error(res?.data?.message, { delay: 100 });
              // }
              reject(res);
            });
        } else {
          resolve();
        }
      });
    });
  };

  const onPublishScenario = () => {
    publishScenario(`${currentScenarioId}`)
      .unwrap()
      .then(data => {
        dispatch(ChatbotRTKService.util.invalidateTags(['GetAllScenarios', 'Package']));
        toast.success(t('KnowledgeBasesCreateConditionalScenario/text-02'), {
          delay: 100,
        });
        setHasUnpublishedData(false);
        dispatch(handleSetBotStatus('ready'));
      });
  };

  const handleManageLivePreview = () => {
    setIsLivePreviewLoading(true);
    setIsLivePreview(prev => !prev);
    setTimeout(() => {
      setIsLivePreviewLoading(false);
    }, 3000);
  };

  const closeMobileModalHandler = () => {
    setShowMobileModal(false);
    localStorage.setItem('VSBuilderMobileModal', JSON.stringify(client?.sum_identification));
  };

  const getToken = () => {
    const token = localStorage.getItem(`LOUISEBOT_token`);

    return token;
  };

  const getFormItems = () => {
    const items = {
      tree: treeData,
      itemData: macBotState.data,
      loading: modifyScenarioStatus.isLoading,
      createScenarioBlock,
      deleteScenarioBlock,
      changeScenarioBlock,
      parentId: macBotState.parentId,
      setShow: () => {
        closeForm(macBotState.parentId);
      },
      config: configData,
    };

    return items;
  };

  const ControlActions = () => {
    const { zoomIn, zoomOut, zoomToElement } = useControls();

    return (
      <div className="tw-w-full tw-h-[36px] tw-bg-gray-50 tw-flex tw-items-center md:tw-justify-between tw-justify-evenly tw-z-0">
        <div className="tw-justify-start tw-items-center tw-flex">
          <Link
            to={`/${MultiLanguageUrl[i18n.language]}/control-panel/chatbots`}
            className="tw-pl-3 tw-pr-4 tw-py-2 tw-bg-transparent md:tw-shadow tw-justify-center tw-items-center tw-gap-2 tw-flex"
          >
            <BiLeftArrowAlt size={20} className="tw-text-gray-400" />
            <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight md:tw-inline-block tw-hidden">
              {t('Wizard/general/key13')}
            </div>
          </Link>
        </div>

        <div className="tw-flex tw-cursor-pointer">
          <PSButton
            id="btn-live-preview"
            onClick={handleManageLivePreview}
            className="tw-px-4 tw-py-2 tw-bg-transparent md:tw-border-x tw-border-none tw-border-y-0 md:tw-shadow md:tw-border tw-border-gray-300 tw-justify-center tw-items-center tw-flex"
          >
            <div className="tw-gap-1 tw-flex">
              {isLivePreviewLoading ? <Spinner size="sm" className="tw-mr-2 tw-text-gray-400" /> : <BiPlay size={20} className="tw-text-gray-400" />}

              <span className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight md:tw-inline-block tw-hidden">
                {isLivePreview ? t('KnowledgeBasesCreateAssistantBot/btn-04') : t('KnowledgeBasesCreateAssistantBot/btn-03')}
              </span>
            </div>
          </PSButton>
          <PSButton
            id="btn-publish"
            className="tw-bg-transparent tw-rounded-none tw-pl-3 tw-pr-4 tw-py-2 tw-justify-center tw-items-center tw-gap-2 tw-flex tw-border-y-0 tw-border-gray-300 md:tw-border-x md:tw-border-solid tw-border-none disabled:tw-border-gray-300"
            onClick={onPublishScenario}
            icon={<BiSlideshow size={20} className={`${hasUnpublishedData ? 'tw-text-blue-400 ' : 'tw-text-gray-400 '}`} />}
          >
            <span
              className={`${
                hasUnpublishedData ? 'tw-text-blue-700 ' : 'tw-text-gray-700 '
              } tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight md:tw-inline-block tw-hidden`}
            >
              {t('Wizard/general/publish')}
            </span>
            {hasUnpublishedData && (
              <span className="tw-flex tw-h-4 tw-w-4 tw-absolute  tw-top-0 md:tw-top-[-5px]  tw-right-[-5px] tw-justify-center tw-items-center">
                <span className="tw-absolute tw-inline-flex tw-h-3 tw-w-3 tw-animate-ping tw-rounded-full tw-bg-red-400 tw-opacity-75"></span>
                <span className="tw-relative tw-inline-flex tw-h-3 tw-w-3 tw-rounded-full tw-bg-red-500"></span>
              </span>
            )}
          </PSButton>
        </div>

        <div className="tw-flex tw-items-center md:tw-justify-start tw-justify-between">
          <PSButton
            id="btn-zoom-out"
            className="md:tw-inline-block tw-hidden tw-bg-transparent tw-rounded-none tw-border-y-0 tw-border-gray-300 md:tw-border-x md:tw-border-solid tw-border-none"
            onClick={() => zoomOut()}
            icon={<BiZoomOut size={20} className="tw-text-gray-500" />}
          />
          <PSButton
            id="btn-home"
            onClick={() => zoomToElement('STARTBUTTON', scale)}
            className="tw-bg-transparent tw-rounded-none tw-border-y-0 tw-border-gray-300 md:tw-border-x tw-border-none"
            icon={<BiHome size={20} className="tw-text-gray-500" />}
          />
          <PSButton
            id="btn-zoom-in"
            className="md:tw-inline-block tw-hidden tw-bg-transparent tw-rounded-none tw-border-y-0 tw-border-gray-300 md:tw-border-x md:tw-border-solid tw-border-none"
            onClick={() => zoomIn()}
            icon={<BiZoomIn size={20} className="tw-text-gray-500" />}
          />
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (!currentScenarioId) {
      return navigate(`/${MultiLanguageUrl[i18n.language]}/control-panel/chatbots`);
    }
  }, [currentScenarioId]);

  useEffect(() => {
    if ((isMobile || window.screen.width <= 480) && isLivePreview && getScenarioDetailData?.id) toast.dismiss();
  }, [isLivePreview]);

  useEffect(() => {
    if (isLivePreview && getScenarioDetailData?.id) {
      dispatch(handleSetLivePreviewTargetBot(getScenarioDetailData?.id));
    } else {
      dispatch(handleSetLivePreviewTargetBot(''));
    }
  }, [isLivePreview, getScenarioDetailData?.id]);

  useEffect(() => {
    window.addEventListener('message', (event: MessageEvent<{ action: string; [key: string]: any }>) => {
      if (event.data.action === 'resizeIframe' && isLivePreview && event.data.width === 0 && event.data.height === 0) {
        dispatch(handleSetLivePreviewTargetBot(''));

        setIsLivePreview(false);
      }
    });

    return () => {
      window.removeEventListener('message', () => {
        dispatch(handleSetLivePreviewTargetBot(''));
        setIsLivePreview(false);
      });
    };
  }, [isLivePreview]);

  const onItemPressed = (item: any) => {
    if (!textScenarioAllowedBlocks.includes(item.type) && item.type !== ENestedObjectType.SuperUserManagerServices)
      return dispatch(handleToggleShowUpgradePlanModal(true));

    setShowPlusModal(false);

    closeForm(macBotState.parentId);

    item.action();
  };
  if (isLoading || isLoadingPage) {
    return <LoadingComponent />;
  }

  if (isError)
    return (
      <Row className=" tw-w-5/6 tw-mx-auto tw-mt-2">
        <Alert type="Error">
          <div className="tw-flex tw-items-center tw-gap-4">
            <p className="m-0">{t(AlertsType.ISKBKO003)}</p>
            <PSButton
              id="btn-scenario-refetch"
              size="sm"
              className="px-3"
              onClick={() => refetch()}
              title={t('KnowledgeBasesCreateConditionalScenario/btn-01')}
            />
          </div>
        </Alert>
      </Row>
    );

  return (
    <TransformWrapper limitToBounds={false} initialScale={scale} minScale={scale}>
      {() => (
        <div className="tw-drop-shadow-lg tw-flex flex-row tw-h-full tw-relative lg:tw-overflow-auto tw-overflow-hidden">
          <div className="tw-w-full tw-flex tw-flex-col tw-justify-between">
            {/* Mobile devices Actions */}
            <div className="md:tw-hidden">
              <ControlActions />
            </div>

            {/* Alert of using mobile devices */}
            {showMobileModal && (
              <div className="md:tw-hidden tw-relative tw-p-2 tw-w-full tw-flex tw-justify-start tw-items-start tw-gap-3 tw-bg-blue-50 tw-rounded-md">
                <BiInfoCircle size={20} className="tw-w-6 tw-text-blue-800" />
                <p className="tw-m-0 tw-w-5/6 tw-text-blue-800 tw-text-sm tw-font-medium tw-leading-tight">{t('KnowledgeBasesCreateAssistantBot/text-03')}</p>
                <BiX size={24} onClick={() => closeMobileModalHandler()} className="tw-absolute tw-top-2 tw-right-2 tw-text-neutral-400 tw-cursor-pointer" />
              </div>
            )}

            <div className="tw-h-full tw-w-full" style={{ backgroundImage: "url('/dotPattern.png')" }}>
              <CreateScenarioTooltip />

              <div className={`editor-pattern ${showPlusModal || hasTrueShowKey(macBotState) ? 'is-open' : ''}`}>
                <React.Fragment>
                  <TransformComponent wrapperClass="customDraggable">
                    <OrgTreeComponent
                      strokeColor="#C1C8CD"
                      strokeWidth="5px"
                      data={data}
                      ref={treeRef}
                      horizontal
                      renderCard={renderCard as any}
                      renderButton={renderButton as any}
                    />
                  </TransformComponent>
                  {/* Desktop actions */}

                  <div className="md:tw-block tw-hidden">
                    <ControlActions />
                  </div>
                </React.Fragment>
              </div>
            </div>
          </div>

          {showPlusModal && (
            <div className="md:tw-w-[550px] tw-w-full tw-absolute tw-right-0 tw-bg-gray-50 tw-h-full tw-z-[1]">
              {/* Header */}
              <div className="tw-flex tw-flex-row tw-justify-between tw-p-4">
                <div className="tw-h-11 tw-pl-2.5 tw-pr-4 tw-py-2.5  tw-justify-center tw-items-center tw-gap-2 tw-inline-flex">
                  <div className="tw-text-gray-800 tw-text-sm tw-font-semibold tw-font-['Lexend'] tw-leading-tight">{t('AddItemModal/key1')}</div>
                </div>

                <button
                  id="btn-close-text-scenario"
                  onClick={() => setShowPlusModal(false)}
                  className="tw-cursor-pointer tw-p-2 tw-bg-gray-100 tw-rounded-[20px] tw-justify-start tw-items-center tw-gap-3 tw-flex tw-border-none tw-text-gray-800"
                  type="button"
                >
                  <BiX className="tw-w-6 tw-h-6 tw-relative" />
                </button>
              </div>
              {/* Content */}
              <div className="tw-flex tw-flex-wrap tw-gap-3 tw-p-1 tw-max-h-[calc(100%-82px)] tw-overflow-y-auto">
                {scenarioMenuFilter().map(item => (
                  <div
                    id={`btn-scenario-name-${item.name}`}
                    role="button"
                    key={item.name}
                    className={`tw-w-full tw-p-3 tw-bg-gray-50 hover:tw-bg-gray-100 tw-border-none tw-rounded-2xl tw-justify-start tw-items-start tw-gap-3 tw-inline-flex`}
                  >
                    <div
                      role="button"
                      tabIndex={0}
                      id={`btn-scenario-item-${item.name}`}
                      className="tw-w-11 tw-h-11 tw-shrink-0 tw-px-1 tw-pt-[5px] tw-pb-[3px] tw-rounded-[99px] tw-justify-center tw-items-center tw-gap-2.5 tw-flex tw-flex-col"
                      style={{ backgroundColor: item.colors.primary }}
                      onClick={() => onItemPressed(item)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          onItemPressed(item);
                        }
                      }}
                    >
                      {item.icon}
                    </div>

                    <div className="tw-flex tw-items-center tw-justify-between tw-w-full tw-gap-1">
                      <div
                        id={`btn-scenario-label-${item.name}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => onItemPressed(item)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            onItemPressed(item);
                          }
                        }}
                        className="tw-grow tw-shrink tw-basis-0 tw-flex-col tw-justify-start tw-items-start tw-gap-1 tw-inline-flex"
                      >
                        <div className="tw-text-gray-800 tw-text-sm tw-font-semibold tw-font-['Lexend'] tw-leading-tight tw-text-left">
                          {ReactHtmlParser(item.label)}
                        </div>
                        <div className="tw-self-stretch tw-text-left tw-text-gray-800 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                          {item.description}
                        </div>
                      </div>

                      {!textScenarioAllowedBlocks.includes(item.type) && item.type !== ENestedObjectType.SuperUserManagerServices ? (
                        <div className="tw-w-11 tw-h-11 tw-p-1 tw-border-none tw-bg-gray-100 tw-rounded-[99px] tw-justify-center tw-items-center tw-gap-2.5 tw-flex tw-flex-col hover:tw-text-primary">
                          <ProBadge variant="small" />
                        </div>
                      ) : (
                        <></>
                      )}
                      <div className="tw-flex tw-items-center tw-gap-3">
                        <ScenarioYoutubeVideo item={item} />
                        <div
                          id={`btn-scenario-plus-${item.name}`}
                          role="button"
                          tabIndex={0}
                          onClick={() => onItemPressed(item)}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              onItemPressed(item);
                            }
                          }}
                          className="tw-w-11 tw-h-11 tw-p-1 tw-border-none tw-bg-gray-100 tw-rounded-[99px] tw-justify-center tw-items-center tw-gap-2.5 tw-flex tw-flex-col hover:tw-text-primary tw-z-[2]"
                        >
                          <BiPlus size={22} />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {macBotState.isShowResponseForm && !showPlusModal && <MacBotResponseForm {...getFormItems()} />}

          {macBotState.isShowUserInputForm && <MacBotUserInputForm {...getFormItems()} />}

          {macBotState.isShowGotoForm && <MacBotGotoForm {...getFormItems()} />}

          {macBotState.isShowButtonForm && <MacBotButtonForm {...getFormItems()} />}

          {macBotState.isShowLinkForm && <TextScenarioLinkForm {...getFormItems()} />}
          {macBotState.isShowVariableForm && <MacBotVariableForm {...getFormItems()} />}
          {macBotState.isShowHTTPRequestForm && <MacBotHTTPRequestForm {...getFormItems()} />}
          {macBotState.isShowMakeWebHookForm && <MacBotMakeWebHookForm {...getFormItems()} />}
          {macBotState.isShowConditionForm && <MacBotConditionForm {...getFormItems()} />}
          {macBotState.isShowSpeechRecording && <MacBotSpeechRecordingForm {...getFormItems()} />}
          {macBotState.isShowOverrideVariableForm && <MacBotOverrideVariableForm {...getFormItems()} />}
          {macBotState.isShowCreateVariableForm && <MacBotCreateVariableForm {...getFormItems()} />}
          {macBotState.isShowLeadForm && <MacBotLeadForm {...getFormItems()} />}
          {macBotState.isShowMediaForm && <MacBotMediaForm {...getFormItems()} />}
          {macBotState.isShowAttachmentForm && <MacBotAttachmentForm {...getFormItems()} />}
          {macBotState.isShowEndOfConversationForm && <EndConversationForm {...getFormItems()} />}
          {macBotState.isShowNotifyMeByEmailForm && <MacBotNotifyMeByEmailForm {...getFormItems()} />}
          {macBotState.isShowReceiveAttachmentForm && <MacBotReceiveAttachmentForm {...getFormItems()} />}
          {macBotState.isShowDelayForm && <DelayForm {...getFormItems()} />}
          {macBotState.isShowSuperUserManagerServices && <SuperUserManagerServicesForm {...getFormItems()} />}
          {macBotState.isShowGetUserInfo && <GetUserInfoForm {...getFormItems()} />}
          {macBotState.isShowRegisterUser && <EditRegisterUserForm {...getFormItems()} />}
          {macBotState.isShowCheckIfUserExists && <CheckIfUserExistsForm {...getFormItems()} />}
          {macBotState.isShowUserCredential && <UserCredentialForm {...getFormItems()} />}
          {macBotState.isShowPassToAi && <MacBotPassToAIForm {...getFormItems()} />}
          {macBotState.isShowSwitchToChatbot && <MacBotSwitchToChatbotForm {...getFormItems()} />}
          {macBotState.isShowAskToAI && <MacBotASKToAIForm {...getFormItems()} />}
          {macBotState.isShowSummarizeConversation && <MacBotSummarizeConversationForm {...getFormItems()} />}
          {macBotState.isShowJSCode && <MacBotJSCodeForm {...getFormItems()} />}
        </div>
      )}
    </TransformWrapper>
  );
};

export default TextScenarioComponent;
